// Thin React-Query wrappers around the fetch client. They're deliberately
// narrow — one hook per endpoint — so call-sites read clearly and we avoid
// reinventing TanStack's built-in retry / staleTime machinery.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { makeClient } from '../api/client'
import type {
  ControlTowerStats,
  DashboardStats,
  Engineer,
  ExceptionDetail,
  ExceptionItem,
  GeoStats,
  Readiness,
  ReasonCode,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'

function useClient() {
  const { accessToken } = useAuth()
  // Cheap: makeClient just curries the getter; safe to recreate per render.
  return makeClient(() => accessToken)
}

// --- stats -----------------------------------------------------------------

export function useControlTowerStats() {
  const client = useClient()
  return useQuery({
    queryKey: ['stats'],
    queryFn: () => client.call<ControlTowerStats>('/api/v1/stats/control-tower'),
    refetchInterval: 10_000,
  })
}

export function useDashboardStats() {
  const client = useClient()
  return useQuery({
    queryKey: ['stats', 'dashboard'],
    queryFn: () => client.call<DashboardStats>('/api/v1/stats/dashboard'),
    refetchInterval: 10_000,
  })
}

export function useGeoStats() {
  const client = useClient()
  return useQuery({
    queryKey: ['stats', 'geo'],
    queryFn: () => client.call<GeoStats>('/api/v1/stats/geo'),
    refetchInterval: 15_000,
  })
}

export function useReadiness(windowHours = 24) {
  const client = useClient()
  return useQuery({
    queryKey: ['stats', 'readiness', windowHours],
    queryFn: () => client.call<Readiness>(`/api/v1/stats/readiness?window_hours=${windowHours}`),
    refetchInterval: 15_000,
  })
}

// --- exceptions ------------------------------------------------------------

export type ExceptionsFilter = {
  severity?: string[] // any of info|warn|high|critical
  code?: string[]
  subject_type?: string
}

export const EXCEPTIONS_PAGE_SIZES = [25, 50, 100, 200] as const
export type ExceptionsPageSize = (typeof EXCEPTIONS_PAGE_SIZES)[number]

function buildExceptionsQueryBase(filter: ExceptionsFilter): URLSearchParams {
  const p = new URLSearchParams()
  if (filter.severity && filter.severity.length) p.set('severity', filter.severity.join(','))
  if (filter.code && filter.code.length) p.set('code', filter.code.join(','))
  if (filter.subject_type) p.set('subject_type', filter.subject_type)
  return p
}

export function useExceptions(
  filter: ExceptionsFilter,
  page: number,
  pageSize: ExceptionsPageSize,
) {
  const client = useClient()
  const p = buildExceptionsQueryBase(filter)
  p.set('limit', String(pageSize))
  p.set('offset', String((page - 1) * pageSize))
  const qs = p.toString()

  return useQuery({
    queryKey: ['exceptions', qs],
    queryFn: () => client.callEnvelope<ExceptionItem[]>(`/api/v1/exceptions?${qs}`),
    refetchInterval: 15_000,
    // Keep the prior page rendered while the next one fetches — no flicker
    // when the user clicks "next".
    placeholderData: keepPreviousData,
  })
}

// Build an absolute URL for the CSV export; used by <a href>, not fetch.
export function buildExceptionsExportPath(
  filter: ExceptionsFilter,
  scope: 'page' | 'all',
  page: number,
  pageSize: ExceptionsPageSize,
): string {
  const p = buildExceptionsQueryBase(filter)
  p.set('scope', scope)
  if (scope === 'page') {
    p.set('limit', String(pageSize))
    p.set('offset', String((page - 1) * pageSize))
  }
  return `/api/v1/exceptions/export.csv?${p.toString()}`
}

export function useExceptionDetail(id: string | null) {
  const client = useClient()
  return useQuery({
    queryKey: ['exception', id],
    queryFn: () => client.call<ExceptionDetail>(`/api/v1/exceptions/${id}`),
    enabled: !!id,
  })
}

// --- intervention mutations ------------------------------------------------

export function useAcknowledge() {
  const client = useClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      client.call<ExceptionItem>(`/api/v1/exceptions/${id}/acknowledge`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['exceptions'] })
      void qc.invalidateQueries({ queryKey: ['exception', id] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useResolve() {
  const client = useClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: string; close_reason: string }) =>
      client.call<ExceptionItem>(`/api/v1/exceptions/${args.id}/resolve`, {
        method: 'POST',
        body: { close_reason: args.close_reason },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exceptions'] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useEscalate() {
  const client = useClient()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (args: { id: string; tier: string; note: string }) =>
      client.call<ExceptionItem>(`/api/v1/exceptions/${args.id}/escalate`, {
        method: 'POST',
        body: { tier: args.tier, note: args.note },
      }),
    onSuccess: (_data, args) => {
      void qc.invalidateQueries({ queryKey: ['exceptions'] })
      void qc.invalidateQueries({ queryKey: ['exception', args.id] })
      void qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

// --- engineers + reason-codes ---------------------------------------------

export function useEngineers() {
  const client = useClient()
  return useQuery({
    queryKey: ['engineers'],
    queryFn: () => client.call<Engineer[]>('/api/v1/engineers?limit=50'),
    refetchInterval: 20_000,
  })
}

export function useReasonCodes() {
  const client = useClient()
  return useQuery({
    queryKey: ['reason-codes'],
    queryFn: () => client.call<ReasonCode[]>('/api/v1/reason-codes'),
    staleTime: 5 * 60_000, // 5 min — they change rarely
  })
}
